import { useCallback, useEffect, useState } from "react";

// ─── Constants ─────────────────────────────────────────────
import { organizationsList } from "./constants";

// ─── Styles ────────────────────────────────────────────────
import styles from "../usersManagement/user_management_styles.module.scss";
import orgStyles from "./Organisation.module.scss";

// ─── Components ────────────────────────────────────────────
import SearchInput from "../../components/common/searchInput";
import Paginations from "../../components/common/Pagination/paginations";
import PrimaryButton, {
  PrimaryButtonType,
} from "../../components/common/PrimaryButton";
import OrganizationListItem from "../../components/organisations/OrganisationListItem";
import OrganizationTableShimmer from "./OrganizationTableShimmer";

// ─── Store ─────────────────────────────────────────────────
import { useOrganisationStore } from "../../providers/organisation/organisation.provider";

// ─── Libraries ─────────────────────────────────────────────
import { useTranslation } from "react-i18next";
import DeleteModal from "../../components/organisations/DeleteModal";
import { IOrganizations } from "../../setup/types/Organisation";
import AddOrganisationModal from "../../components/organisations/AddOrganisation";

const Organizations = () => {
  const { t } = useTranslation();

  // ─── Local states ─────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // ─── Zustand Store Selectors ─────────────────────────────
  const setSelectedOrganization = useOrganisationStore(
    (state) => state.setSelectedOrganization
  );
  const organizations = useOrganisationStore((state) => state.organizations);
  const page = useOrganisationStore((state) => state.page);
  const pageCount = useOrganisationStore((state) => state.pageCount);
  const loading = useOrganisationStore((state) => state.loading);
  const setPage = useOrganisationStore((state) => state.setPage);
  const searchOrganisation = useOrganisationStore(
    (state) => state.searchOrganisation
  );

  // ─── Local State ─────────────────────────────────────────
  const [showADDORGANIZATIONModal, setShowADDORGANIZATIONModal] =
    useState(false);
  const [searchValue, setSearchValue] = useState("");

  // ─── Handlers ────────────────────────────────────────────
  const toggleCreateNewOrganisation = useCallback(() => {
    setShowADDORGANIZATIONModal((prev) => !prev);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      searchOrganisation(searchValue);
    }, 1000);

    return () => {
      clearTimeout(handler); // cleanup on value change/unmount
    };
  }, [searchValue, searchOrganisation]);

  const handlePaginationChange = (value: number) => {
    setPage(value);
    searchOrganisation(searchValue);
  };

  const toggleDeleteModal = useCallback(
    (org?: IOrganizations) => {
      if (org && !showDeleteModal) setSelectedOrganization(org);
      else setSelectedOrganization(undefined);
      setShowDeleteModal((prev) => !prev);
    },
    [setSelectedOrganization, showDeleteModal]
  );

  // ─── Render ──────────────────────────────────────────────
  return (
    <div className={styles["page_layout"]}>
      <h1>{t("organisation_title")}</h1>

      {/* ─── Header Section ───────────────────────────── */}
      <div className={orgStyles.header_container}>
        <div className={orgStyles.right_section}>
          <h3>{t("organisation_list")}</h3>
        </div>

        <div className={orgStyles.left_section}>
          <SearchInput
            dir="ltr"
            placeholder={organizationsList.FIND_A_ORGANIZATION}
            value={searchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={(event) => {
              setSelectedOrganization();
              setSearchValue(event.target.value);
            }}
          />

          <PrimaryButton
            buttonType={PrimaryButtonType.ROUNDED}
            onClick={toggleCreateNewOrganisation}
            style={{ width: "157px", height: "35px" }}
            Lefticon={<img src="/assets/images/svg/plus.svg" alt="add" />}>
            <p className={orgStyles["button_text"]}>
              {t("add_new_organisation")}
            </p>
          </PrimaryButton>
        </div>
      </div>

      {/* ─── Organization Table ────────────────────────── */}
      <table className={orgStyles.table}>
        <thead>
          <tr className={orgStyles.table_header}>
            <th className={orgStyles.hint}>{t("logo")}</th>
            <th className={orgStyles.hint}>{t("organisation_name")}</th>
            <th className={orgStyles.hint}>{t("code")}</th>
            <th className={orgStyles.hint}>{t("website_link")}</th>
            <th className={orgStyles.hint}>{t("description")}</th>
            <th className={orgStyles.hint}>{t("procedures")}</th>
          </tr>
        </thead>

        {loading ? (
          <OrganizationTableShimmer />
        ) : (
          <tbody className={styles.table_body}>
            {organizations?.map((organizationItem, index) => (
              <OrganizationListItem
                key={index}
                organisation={organizationItem}
                onEdit={() => {
                  setShowADDORGANIZATIONModal(true);
                  setSelectedOrganization(organizationItem);
                }}
                handleDeleteOrganisation={() =>
                  toggleDeleteModal(organizationItem)
                }
              />
            ))}
          </tbody>
        )}
      </table>

      {/* ─── Add/Edit Modal ───────────────────────────── */}
      <AddOrganisationModal
        showModal={showADDORGANIZATIONModal}
        closeModal={() => {
          setSelectedOrganization(undefined);
          setShowADDORGANIZATIONModal(false);
        }}
      />

      {/* ─── Delete Modal ───────────────────────────── */}
      <DeleteModal showModal={showDeleteModal} closeModal={toggleDeleteModal} />

      {/* ─── Pagination ───────────────────────────────── */}
      {organizations && organizations?.length > 0 && (
        <div className="paginatorContainer">
          <Paginations
            pagesCount={pageCount}
            length={pageCount}
            page={page}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      )}
    </div>
  );
};

export default Organizations;
